import { PHONE_COUNTRY_CODE } from 'src/app/shared/constants/phone-country-code.constants';

/**
 * Formats a phone number according to a specified pattern.
 *
 * Examples:
 * 0402 123 456 - mobile pattern.
 * +61 402 123 456 - international mobile pattern.
 * 02 1234 5678 - landline pattern.
 * +61 2 1234 5678 - international landline pattern.
 *
 * @param phoneNumber - phone number to be formatted
 * @param phoneNumberPattern - mobile or landline
 */
export function formatPhoneNumber(phoneNumber: string, phoneNumberPattern: 'landline' | 'mobile' = 'mobile'): string {
  let formattedPhoneNumber = '';

  if (phoneNumber === `+${PHONE_COUNTRY_CODE.AU}`) {
    phoneNumber = '';
  }

  if (phoneNumber === '+') {
    phoneNumber = `+${PHONE_COUNTRY_CODE.AU} `;
  }

  const isInternationalFormat = phoneNumber.startsWith(`+${PHONE_COUNTRY_CODE.AU}`);
  const phoneNumberDigits = phoneNumber.replace(/\D/g, '');

  if (phoneNumberPattern === 'landline') {
    formattedPhoneNumber = formatLandlinePhoneNumber(phoneNumberDigits, isInternationalFormat);
  } else {
    formattedPhoneNumber = formatMobilePhoneNumber(phoneNumberDigits, isInternationalFormat);
  }
  return formattedPhoneNumber;
}

/**
 * Formats a mobile phone number based on the provided digits.
 *
 * @param phoneNumberDigits - The digits of the phone number to be formatted.
 * @param isInternationalFormat - indicates if +61 prefix is in use.
 */
export function formatMobilePhoneNumber(phoneNumberDigits: string, isInternationalFormat: boolean): string {
  if (phoneNumberDigits.length === 0) {
    return '';
  }
  if (isInternationalFormat) {
    phoneNumberDigits = phoneNumberDigits.substring(2);
  } else if (phoneNumberDigits[0] === '0') {
    phoneNumberDigits = phoneNumberDigits.substring(1);
  }
  const phoneNumberPrefix = isInternationalFormat ? `+${PHONE_COUNTRY_CODE.AU} ` : '0';

  if (phoneNumberDigits.length <= 3) {
    return `${phoneNumberPrefix}${phoneNumberDigits}`;
  }
  if (phoneNumberDigits.length <= 6) {
    return `${phoneNumberPrefix}${phoneNumberDigits.substring(0, 3)} ${phoneNumberDigits.substring(3)}`;
  }

  return `${phoneNumberPrefix}${phoneNumberDigits.substring(0, 3)} ${phoneNumberDigits.substring(
    3,
    6,
  )} ${phoneNumberDigits.substring(6, 9)}`;
}

/**
 * Formats a landline phone number based on the provided digits and action.
 *
 * @param phoneNumberDigits - The digits of the phone number to be formatted.
 * @param isInternationalFormat - indicates if +61 prefix is in use.
 */
export function formatLandlinePhoneNumber(phoneNumberDigits: string, isInternationalFormat: boolean): string {
  if (phoneNumberDigits.length === 0) {
    return '';
  }

  if (isInternationalFormat) {
    phoneNumberDigits = phoneNumberDigits.substring(2);
  } else if (phoneNumberDigits[0] === '0') {
    phoneNumberDigits = phoneNumberDigits.substring(1);
  }

  const phoneNumberPrefix = isInternationalFormat ? `+${PHONE_COUNTRY_CODE.AU} ` : '0';

  if (phoneNumberDigits.length <= 1) {
    return `${phoneNumberPrefix}${phoneNumberDigits}`;
  }
  if (phoneNumberDigits.length <= 5) {
    return `${phoneNumberPrefix}${phoneNumberDigits.substring(0, 1)} ${phoneNumberDigits.substring(1)}`;
  }
  return `${phoneNumberPrefix}${phoneNumberDigits.substring(0, 1)} ${phoneNumberDigits.substring(
    1,
    5,
  )} ${phoneNumberDigits.substring(5, 9)}`;
}

/**
 * Used to determine if a number is a local mobile number or not (excludes international mobiles).
 * Uses prefix of `04` to determine if its a valid local mobile number
 *
 * @example
 * 0402 123 456 - mobile pattern, true
 * +61 402 123 456 - international mobile pattern, false
 * 02 1234 5678 - landline pattern, false
 *
 * @param phoneNumberDigits - The phone number we are checking against.
 * @returns True if its a local australia mobile number, else false.
 */
export function isLocalMobileNumber(phoneNumberDigits: string): boolean {
  const prefix = phoneNumberDigits.substring(0, 2);
  if (prefix === '04') {
    return true;
  }
  return false;
}

/**
 * If a phone number is identified as a local mobile number, we will attempt to remove the leading `0`
 * and prepend a `+61`. This method assumes all local mobile numbers on platform are Australian.
 *
 * If the number is not a loca mobile number, simply return it with whitespace removed
 *
 * @param phoneNumberDigits - A phone number we wish to attempt to convert.
 * @returns The converted mobile number, or the original number provided.
 */
export function attemptLocalToInternationalMobileNumber(phoneNumberDigits: string): string {
  const cleanedPhoneNumber = phoneNumberDigits.replace(/\s/g, '');
  const phoneNumberLength = cleanedPhoneNumber.length;
  if (isLocalMobileNumber(cleanedPhoneNumber)) {
    return `${'+'}${PHONE_COUNTRY_CODE.AU}${cleanedPhoneNumber.substring(1, phoneNumberLength)}`.replace(/\s/g, '');
  }

  return cleanedPhoneNumber;
}
